import React, { useState } from 'react';
import { uniq } from 'ramda';
import classNames from 'classnames';
import { Stack, StackProps, Text, Select, Divider, Badge } from '@moda/om';
import { SizeGuideFragment } from '../../generated/types';
import {
  REGIONS,
  getSizeRegion,
  groupSizesByRegion,
  usePreferredRegion
} from './groupSizesByRegion';
import './SizeGuide.scss';

type Props = Omit<StackProps, 'space'> & {
  sizes: SizeGuideFragment[];
  sizeEquivalents: string[][];
  fitRecommendation?: string | null;
};

export const SizeGuide: React.FC<Props> = ({
  className,
  sizes,
  sizeEquivalents,
  fitRecommendation,
  ...rest
}) => {
  const preferredRegion = usePreferredRegion();
  const productSizeRegion = getSizeRegion(sizes[0]?.size);
  const sizesByRegion = sizeEquivalents.map(groupSizesByRegion);
  const availableRegions = [...REGIONS.map(region => region.name), 'Standard'].filter(
    region => sizesByRegion[0]?.[region] && region !== productSizeRegion
  );

  const defaultRegion = availableRegions.includes(preferredRegion.name)
    ? preferredRegion.name
    : REGIONS.find(region => availableRegions.includes(region?.name))?.name || 'Standard';

  const [region, setRegion] = useState(defaultRegion);

  return (
    <Stack className={classNames('SizeGuide', className)} space={4} {...rest}>
      <Text data-test-id="size-guide-copy">
        {availableRegions.length > 1 && 'Select your region to convert your size.'} Size and cut may
        vary between brands. Use this chart as a general guide.
      </Text>

      {availableRegions.length > 1 && (
        <Select
          options={availableRegions.map(region => ({ value: region, label: region }))}
          value={region}
          onChange={setRegion}
          data-test-id="size-guide-selector"
        />
      )}

      <Divider />

      {fitRecommendation && <Badge theme="noise">{fitRecommendation}</Badge>}

      <Stack data-test-id="size-guide-sizes" className="SizeGuide__sizes" space={0}>
        <Stack
          data-test-id="size-guide-head-row"
          className="SizeGuide__head-row"
          space={2}
          direction="horizontal"
        >
          <Text className="SizeGuide__cell" treatment="bold1">
            {productSizeRegion.toUpperCase()}
          </Text>
          <Text className="SizeGuide__cell" treatment="bold1">
            {region.toUpperCase()}
          </Text>
        </Stack>

        {sizes.map(({ size }) => {
          if (!size) return null;

          const equivalents = uniq(
            sizesByRegion
              .filter(sizes =>
                sizes[productSizeRegion]?.some(
                  originalSize => originalSize === size || originalSize.split('/').includes(size)
                )
              )
              .map(sizes => sizes[region])
              .flat()
          );

          return (
            <Stack
              className="SizeGuide__row"
              key={size}
              space={2}
              direction="horizontal"
              data-test-id="size-guide-size"
              data-test-size={size}
              data-test-equivalents={equivalents.join(', ')}
            >
              <Text className="SizeGuide__cell">{size}</Text>
              <Text className="SizeGuide__cell">{equivalents.join(', ')}</Text>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};
